import { useMemo } from 'react';

/** Mobile apps that we support in-browser app detection for */
export const InAppBrowser = {
  FACEBOOK: 'facebook',
  INSTAGRAM: 'instagram',
  TWITTER: 'twitter',
  LINKEDIN: 'linkedin',
  TIKTOK: 'tiktok',
  SNAPCHAT: 'snapchat',
  LINE: 'line',
  WECHAT: 'wechat',
  PINTEREST: 'pinterest',
  REDDIT: 'reddit',
  MESSENGER: 'messenger',
  WHATSAPP: 'whatsapp',
  TELEGRAM: 'telegram',
  DISCORD: 'discord',
  SLACK: 'slack',
  // UNKNOWN: 'unknown', // Intended for "likely to be a mobile app, but we don't recognise it"
} as const;
export type InAppBrowser = (typeof InAppBrowser)[keyof typeof InAppBrowser];

/**
 * Hook to detect which in-app browser the user is currently using.
 * Returns `null` if no specific match is found.
 *
 * @example
 * const inAppBrowser = useInAppBrowser();
 * if (inAppBrowser === InAppBrowser.FACEBOOK) {
 *   // Handle Facebook in-app browser specific behavior
 * }
 * if (inAppBrowser) {
 *   // User is in some in-app browser
 * }
 */
export const useInAppBrowser = (): InAppBrowser | null => {
  const inAppBrowser = useMemo(() => {
    if (typeof window === 'undefined' || !window.navigator) {
      return null;
    }

    return detectInAppBrowser(window.navigator.userAgent, window);
  }, []);

  return inAppBrowser;
};

/**
 * Detects which in-app browser is being used based on the user agent string.
 *
 * Falls back to `null`.
 */
const detectInAppBrowser = (userAgent: string, windowObj?: Window): InAppBrowser | null => {
  const ua = userAgent.toLowerCase();

  if (ua.includes('linkedin')) {
    return InAppBrowser.LINKEDIN;
  }

  if (ua.includes('fban') || ua.includes('fbav') || ua.includes('fb_iab')) {
    return InAppBrowser.FACEBOOK;
  }

  if (ua.includes('instagram')) {
    return InAppBrowser.INSTAGRAM;
  }

  if (ua.includes('tiktok') || ua.includes('musical_ly') || ua.includes('bytelocale')) {
    return InAppBrowser.TIKTOK;
  }

  if (ua.includes('snapchat')) {
    return InAppBrowser.SNAPCHAT;
  }

  if (ua.includes('line/')) {
    return InAppBrowser.LINE;
  }

  if (ua.includes('micromessenger')) {
    return InAppBrowser.WECHAT;
  }

  if (ua.includes('pinterest')) {
    return InAppBrowser.PINTEREST;
  }

  if (ua.includes('messenger')) {
    return InAppBrowser.MESSENGER;
  }

  if (ua.includes('whatsapp')) {
    return InAppBrowser.WHATSAPP;
  }

  // Only detectable on Android (no distinct user-agent in iOS)
  if (ua.includes('twitter')) {
    return InAppBrowser.TWITTER;
  }

  // Telegram: Check for window objects (more reliable) or UA string
  if (windowObj) {
    // Android detection
    if ('TelegramWebview' in windowObj) {
      return InAppBrowser.TELEGRAM;
    }
    // iOS detection
    if ('TelegramWebviewProxy' in windowObj || 'TelegramWebviewProxyProto' in windowObj) {
      return InAppBrowser.TELEGRAM;
    }
  }
  if (ua.includes('telegram')) {
    return InAppBrowser.TELEGRAM;
  }

  /* The below are considered unreliable */

  if (ua.includes('reddit')) {
    return InAppBrowser.REDDIT;
  }

  if (ua.includes('discord')) {
    return InAppBrowser.DISCORD;
  }

  if (ua.includes('slack')) {
    return InAppBrowser.SLACK;
  }

  // // Check if it's likely an in-app browser but we don't recognize it
  // // In-app browsers often have certain characteristics
  // if (
  //   (ua.includes('wv') || // WebView indicator
  //     ua.includes('webview') ||
  //     ua.includes('[fb') ||
  //     ua.includes('[ln')) &&
  //   !ua.includes('safari') // Regular Safari is not an in-app browser
  // ) {
  //   return InAppBrowser.UNKNOWN;
  // }

  return null;
};
